<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import ButtonIcon from '@/components/common/ButtonIcon.vue';

const props = defineProps({
    size: {
        type: String,
        default: 'medium'
    },
    header: {
        type: String,
        default: ''
    },
    height: {
        type: String,
        default: ''
    },
    accentColour: {
        type: String,
        default: null
    },
    showHeader: {
        type: Boolean,
        default: true
    },
    contentClass: {
        type: Boolean,
        default: true
    },
    showFooter: {
        type: Boolean,
        default: true
    },
    showBackButton: {
        type: Boolean,
        default: true
    },
    showExitButton: {
        type: Boolean,
        default: true
    },
    overflow: {
        type: [String, Array],
        default: 'auto'
    },
    disableClose: {
        type: Boolean,
        default: false
    },
    flex: {
        type: Boolean,
        default: false
    }
});

const emits = defineEmits<{
    'open': [],
    'close': []
}>();

const wrapperRef = ref();
const modalRef = ref();

onMounted(() => {
    (modalRef.value as HTMLElement)?.focus();
    emits('open');
});

function isCursorOutsideBox(event: Event): void {
    if (props.disableClose) return;

    const wrapper = wrapperRef.value as HTMLDivElement;
    const modal = modalRef.value as HTMLDivElement;

    if ((event.target === wrapper || event.target === modal)) {
        emits('close');
    }
}
</script>

<template>
    <div
        ref="modalRef"
        class="sy-modal-mask"
        tabindex="0"
        @keyup.esc="emits('close')"
        @mouseup.prevent
        @mousedown="isCursorOutsideBox($event)"
    >
        <div
            ref="wrapperRef"
            :class="['sy-modal-wrapper', height]"
        >
            <div :class="['sy-modal-container',`overflow-${overflow}`, size, height, accentColour, { 'flex-container': flex }]">
                <div v-if="showHeader" class="sy-modal-header">
                    <div class="margin-right-6">
                        <slot name="header">
                            <h2 :class="['display-inline', { 'sy-type-xlarge' : size === 'xsmall' || size === 'small' }]">{{ header }}</h2>
                            <slot name="header-icons"></slot>
                        </slot>
                    </div>
                    <div class="sy-modal-icons flex-container align-top">
                        <slot name="modal-icons"></slot>
                        <ButtonIcon
                            v-if="showExitButton"
                            icon="x"
                            :disabled="disableClose"
                            class="margin-left-3"
                            data-test="CloseModal"
                            @click="$emit('close')"
                        />
                    </div>
                </div>
                <div :class="['sy-modal-body padding-vertical-5', `overflow-${overflow}`, height, { 'sy-padded-modal' : contentClass }, { 'flex-child-grow flex-container': flex }]">
                    <slot></slot>
                </div>
                <div v-if="showFooter" :class="['sy-modal-footer flex-container align-middle align-justify', { 'padding-top' : !contentClass }]">
                    <slot name="back-button">
                        <button
                            v-if="showBackButton"
                            type="button"
                            class="button secondary clear"
                            :disabled="disableClose"
                            data-test="exit"
                            @click="emits('close')"
                        >
                            {{ $t('system.back') }}
                        </button>
                        <div v-else></div>
                    </slot>
                    <slot name="footer"></slot>
                </div>
            </div>
        </div>
    </div>
</template>
